/**
 * One session: view, finish a live stub, edit, delete, and launch the debrief.
 * Reads from the cached tracker-home list (bounded) rather than a dedicated
 * fetch — the home query already has every session.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { LiveSession, LogSessionInput, UpdateSessionInput } from "@/shared/models";
import {
  deleteSession,
  getTrackerHome,
  listHands,
  updateSession,
} from "@/shared/api";
import { debriefRoute, handAnalysisRoute } from "@/shared/chatRoute";
import { shortDate } from "@/shared/format";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { ChevronRight, Plus, Search, Snowflake, Trash2 } from "@/shared/components/icons";
import { BackLink } from "@/shared/components/BackLink";
import { Button, buttonClass } from "@/shared/components/Button";
import { PageContainer } from "@/shared/components/layout/PageContainer";
import { SessionForm } from "@/features/tracker/components/SessionForm";
import {
  durationLabel,
  money,
  moneyAbs,
  netToneClass,
  recentDistinct,
} from "@/features/tracker/lib/trackerFormat";

export function SessionDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["tracker-home", null],
    queryFn: () => getTrackerHome(),
    staleTime: 30_000,
  });

  const session = data?.sessions.find((s) => s.id === id);

  const updateMutation = useMutation({
    mutationFn: (input: UpdateSessionInput) => updateSession(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracker-home"] });
      queryClient.invalidateQueries({ queryKey: ["gamification"] });
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracker-home"] });
      navigate("/tracker");
    },
  });

  if (isLoading) {
    return (
      <PageContainer width="narrow">
        <p className="text-sm text-text-muted">Loading…</p>
      </PageContainer>
    );
  }
  if (!session) {
    return (
      <PageContainer width="narrow">
        <p className="text-sm text-text-muted">Session not found.</p>
        <Link to="/tracker" className="text-sm text-accent">← Back to tracker</Link>
      </PageContainer>
    );
  }

  const isLive = session.status === "live";
  const showForm = isLive || editing;

  return (
    <PageContainer width="narrow" space={false} className="space-y-5">
      <BackLink to="/tracker" label="Tracker" />

      {showForm ? (
        <section className="rounded-2xl border border-border bg-surface p-5 space-y-4">
          <h1 className="text-lg font-semibold text-text">
            {isLive ? "Finish this session" : "Edit session"}
          </h1>
          {isLive && session.intention && (
            <p className="text-sm text-text-muted italic">
              Your intention: “{session.intention}”
            </p>
          )}
          <SessionForm
            initial={initialFromSession(session)}
            submitting={updateMutation.isPending}
            error={updateMutation.isError ? "Couldn't save — try again." : null}
            submitLabel={isLive ? "Finish & save" : "Save changes"}
            stakeOptions={recentDistinct(
              (data?.sessions ?? [])
                .filter((s) => s.gameType === "cash")
                .map((s) => s.stakes),
            )}
            locationOptions={recentDistinct(
              (data?.sessions ?? []).map((s) => s.location),
            )}
            onSubmit={(input) =>
              updateMutation.mutate({ ...input, status: "complete" })
            }
            onCancel={isLive ? undefined : () => setEditing(false)}
          />
        </section>
      ) : (
        <SessionSummary
          session={session}
          onEdit={() => setEditing(true)}
        />
      )}

      {/* Debrief CTA — available once the session is complete */}
      {!showForm && (
        <DrillLaunchLink
          to={debriefRoute(session.id)}
          className={buttonClass({ variant: "primary", fullWidth: true })}
        >
          <Snowflake size={16} strokeWidth={2} />
          {session.debriefConversationId ? "Continue debrief" : "Debrief with your coach"}
        </DrillLaunchLink>
      )}

      {/* Hands you remember — the doorway into the standalone Hand Analyzer,
          linked to this session so the debrief coach sees them. */}
      {!showForm && <SessionHands sessionId={session.id} />}

      {/* Delete */}
      {!showForm && (
        <div className="pt-2">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-muted">Delete this session?</span>
              <Button
                variant="danger"
                size="sm"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                Delete
              </Button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-sm text-text-muted hover:text-text"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-danger transition"
            >
              <Trash2 size={15} strokeWidth={1.75} />
              Delete session
            </button>
          )}
        </div>
      )}
    </PageContainer>
  );
}

function SessionSummary({
  session,
  onEdit,
}: {
  session: LiveSession;
  onEdit: () => void;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-text-muted">
            {session.title && session.stakes ? `${session.stakes} · ` : ""}
            {shortDate(session.startedAt)} · {durationLabel(session.durationMinutes)}
            {session.location && ` · ${session.location}`}
          </div>
          <h1 className="mt-0.5 text-xl font-semibold text-text">
            {session.title ??
              session.stakes ??
              (session.gameType === "tournament" ? "Tournament" : "Session")}
          </h1>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold tabular-nums ${netToneClass(session.net)}`}>
            {money(session.net)}
          </div>
          <div className="text-xs text-text-muted">
            {moneyAbs(session.buyIn)} in · {moneyAbs(session.cashOut)} out
          </div>
        </div>
      </div>

      {session.notes && (
        <div className="rounded-lg border border-border bg-bg px-3 py-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Notes</div>
          <div className="mt-0.5 text-sm text-text whitespace-pre-wrap">{session.notes}</div>
        </div>
      )}

      {session.intention && (
        <div className="rounded-lg border border-border bg-bg px-3 py-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Intention</div>
          <div className="text-sm text-text">“{session.intention}”</div>
          {session.intentionHeld !== null && (
            <div className="mt-0.5 text-xs text-text-muted">
              {session.intentionHeld ? "✓ Held" : "✗ Didn't hold"}
            </div>
          )}
        </div>
      )}

      {session.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {session.tags.map((t) => (
            <span
              key={t}
              className="rounded-full border border-border px-2.5 py-1 text-xs text-text-muted"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {session.notableHands.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">
            Notable hands
          </div>
          {session.notableHands.map((h, i) => (
            <div key={i} className="text-sm text-text">
              · {h.summary}
              {h.outcome && (
                <span className="text-text-muted"> ({h.outcome})</span>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onEdit}
        className="text-sm text-text-muted hover:text-text transition"
      >
        Edit details
      </button>
    </section>
  );
}

/** "Hands you remember" — the tracker's doorway into the standalone Hand
 *  Analyzer. Lists hands attached to this session + a CTA to build a new one
 *  (linked to the session). Building is free; the per-hand grade is paid, so the
 *  analyze action routes through DrillLaunchLink. */
function SessionHands({ sessionId }: { sessionId: string }) {
  const { data: hands } = useQuery({
    queryKey: ["hands", sessionId],
    queryFn: () => listHands(sessionId),
    staleTime: 30_000,
  });

  return (
    <section className="rounded-2xl border border-border bg-surface p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Search size={15} strokeWidth={1.75} className="text-accent" />
        <h2 className="text-sm font-semibold text-text">Hands you remember</h2>
      </div>

      {hands && hands.length > 0 ? (
        <ul className="space-y-2">
          {hands.map((h) => (
            <li key={h.id}>
              <DrillLaunchLink
                to={handAnalysisRoute(h.id)}
                className="flex items-center gap-2 rounded-lg border border-border bg-bg px-3 py-2 hover:border-accent/40 transition"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-text">
                    {h.title ?? h.summary}
                  </div>
                  {h.title && (
                    <div className="truncate text-xs text-text-muted">{h.summary}</div>
                  )}
                </div>
                {h.analyzed && (
                  <span className="shrink-0 rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[11px] text-accent">
                    Analyzed
                  </span>
                )}
                <ChevronRight size={15} strokeWidth={1.75} className="shrink-0 text-text-muted" />
              </DrillLaunchLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-muted">
          Got a hand you specifically remember from this session? Build it out and
          have your coach grade the line.
        </p>
      )}

      <Link
        to={`/analyzer/new?session_id=${sessionId}`}
        className={buttonClass({ variant: "secondary", fullWidth: true })}
      >
        <Plus size={16} strokeWidth={2} />
        Build a hand you remember
      </Link>
    </section>
  );
}

function initialFromSession(session: LiveSession): Partial<LogSessionInput> {
  return {
    gameType: session.gameType,
    stakes: session.stakes ?? "",
    buyIn: session.status === "complete" ? session.buyIn : undefined,
    cashOut: session.status === "complete" ? session.cashOut : undefined,
    durationMinutes: session.durationMinutes || undefined,
    startedAt: session.startedAt,
    title: session.title,
    notes: session.notes,
    location: session.location,
    tags: session.tags,
  };
}
